import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { createPortal } from "react-dom";

import { anchorClassNames } from "@/react/helpers/cssAnchorHelpers";

export interface PortalProps {
  children: ReactNode;
  className?: string;
  disablePortal?: boolean;
  element?: Element;
  selectorQuery?: string;
}

export const Portal = ({
  children,
  selectorQuery,
  element,
  className,
  disablePortal = false,
}: PortalProps) => {
  const ref = useRef<Element | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (disablePortal) {
      setIsLoading(false);
      return;
    }

    if (element) {
      // create a portal to element
      ref.current = element;
    } else if (selectorQuery) {
      // create a portal to element with id
      ref.current = document.querySelector(selectorQuery);
    } else {
      // create a new element under body and create a portal to it.
      ref.current = document.createElement("div");
      if (className) {
        ref.current.classList.add(...className.split(" "));
      }
      const portalCls = anchorClassNames("react-portal");
      if (portalCls) ref.current.classList.add(portalCls);
      document.body.appendChild(ref.current);
    }
    setIsLoading(false);
    return () => {
      // remove the element if was created by this component
      if (ref.current && !selectorQuery && !element) {
        ref.current.remove();
      }
    };
  }, [disablePortal]);

  if (isLoading) {
    return null;
  }

  if (disablePortal) {
    return <>{children}</>;
  }

  if (!ref.current) {
    return null;
  }

  return <>{createPortal(children, ref.current)}</>;
};
